import Direction2D from './Direction2D';
import Direction3D from './Direction3D';
import Line2D from './Line2D';
import Plane3D from './Plane3D';
import Point3D from './Point3D';
import { distanceEquals } from './units';
import Vector3D from './Vector3D';

class Line3D {
  /**
   * A direction parallel to the line.
   */
  readonly direction: Direction3D;

  /**
   * The point on the line closest to the origin.
   */
  readonly pointNearestOrigin: Point3D;

  constructor(direction: Direction3D, pointOnLine: Point3D) {
    this.direction = direction;
    this.pointNearestOrigin = pointOnLine.rejectDirection(direction);
  }

  equals(other: Line3D) {
    const linesAreParallel = this.isParallelTo(other);
    const basePointIsOnLine1 = other.contains(this.pointNearestOrigin);
    const basePointIsOnLine2 = this.contains(other.pointNearestOrigin);

    return linesAreParallel && (basePointIsOnLine1 || basePointIsOnLine2);
  }

  isParallelTo(other: Line3D) {
    return this.direction.isParallelTo(other.direction);
  }

  static fromPoints(point0: Point3D, point1: Point3D) {
    const direction = Direction3D.fromPointToPoint(point0, point1);
    return direction && new Line3D(direction, point0);
  }

  /** Returns the point at which this line intersects the passed line */
  intersectWithLine(passedLine: Line3D) {
    if (this.equals(passedLine)) return this as Line3D;

    if (this.isParallelTo(passedLine) || !this.isCoplanarWith(passedLine)) return null;

    // Derived from http://mathworld.wolfram.com/Line-LineIntersection.html
    const directionVectorA = this.direction.toPoint();
    const directionVectorB = passedLine.direction.toPoint();
    const basePointDiffVectorC = passedLine.pointNearestOrigin.subtract(this.pointNearestOrigin);
    const crossProductCB = basePointDiffVectorC.crossProduct(directionVectorB);
    const crossProductAB = directionVectorA.crossProduct(directionVectorB);
    const crossProductABMagnitudeSquared = crossProductAB.magnitude ** 2;
    const dotProductOfCrossProducts = crossProductCB.dotProduct(crossProductAB);
    const solutionVariable = dotProductOfCrossProducts / crossProductABMagnitudeSquared;
    const intersectionPoint = this.getPointAlongLine(solutionVariable) as Point3D;
    return intersectionPoint;
  }

  /** Note that this method differs significantly from the server-side implementation. */
  isCoplanarWith(line: Line3D) {
    const normal = this.direction.crossProductDirection(line.direction);
    // no cross product means the lines are parallel or coincident
    if (!normal) return true;

    // Otherwise, the normal refers to the shared normal of the planes containing the lines.
    // Iff the lines both project to be the same distance along that normal, they lie in the same plane.
    const normalAsPoint = normal.toPoint();
    return distanceEquals(
      this.pointNearestOrigin.dotProduct(normalAsPoint),
      line.pointNearestOrigin.dotProduct(normalAsPoint),
    );
  }

  /** Determines if the line contains the point */
  contains(point: Point3D) {
    return distanceEquals(point.distanceToLine(this), 0);
  }

  /** Returns the point a given distance along the line. */
  getPointAlongLine(distance: number) {
    return this.pointNearestOrigin.translate(this.direction.toVector(distance));
  }

  translate(vector: Vector3D): Line3D {
    return new Line3D(this.direction, this.pointNearestOrigin.translate(vector));
  }

  reverse() {
    return new Line3D(this.direction.reverse(), this.pointNearestOrigin);
  }

  reverseIf(condition: boolean) {
    return condition ? this.reverse() : this;
  }

  orientWith(direction: Direction3D) {
    return this.reverseIf(this.direction.dotProduct(direction) < 0);
  }

  projectTo2D(): Line2D | null {
    const direction = this.direction.projectTo2D();
    if (direction.equals(Direction2D.noDirection)) return null;
    return new Line2D(this.pointNearestOrigin.projectTo2D(), direction);
  }

  projectOntoPlane(plane: Plane3D): Line3D | null {
    const direction = this.direction.reject(plane.halfSpace.direction);
    return direction && new Line3D(direction, this.pointNearestOrigin.projectOntoPlane(plane));
  }
}

export default Line3D;
